import React, { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import type { DailyData } from '../../types';
// Widget is mini-only now; no size type needed
import { fmtMoney } from '../../utils/formatters';

import type { TimeRange } from '../../types/dashboard';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';
import { normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';

interface AveragePerShareGainLossWidgetProps {
  dailyData?: DailyData[];
  trades?: any[];
  timeRange?: TimeRange;
  avgPrice?: number;
}

export const AveragePerShareGainLossWidget: React.FC<AveragePerShareGainLossWidgetProps> = ({
  dailyData = [],
  trades = [],
  timeRange = 'month',
}) => {
  const { totalVolume, avgPerShare, impact } = useMemo(() => {
    const totalPnl = (dailyData || []).reduce((acc, d) => acc + (d.pnl || 0), 0);
    const totalVolume = (dailyData || []).reduce((acc, d) => acc + (d.volume || 0), 0);
    const avgPerShare = totalVolume === 0 ? 0 : totalPnl / totalVolume;
    const impact = avgPerShare * totalVolume;
    return { totalVolume, avgPerShare, impact };
  }, [dailyData]);

  const dynamicAvgPrice = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return null;
    const activeTrades = filterTradesForDashboardRange(trades as Record<string, any>[], timeRange as any);
    let totalNotional = 0;
    let totalQty = 0;

    for (const trade of activeTrades) {
      if (normalizeTradeStatus(trade) === 'open') continue;
      const entryPrice = Number(
        trade?.entryPrice ??
          trade?.avgEntryPrice ??
          trade?.avgEntry ??
          trade?.entry_price ??
          trade?.price ??
          trade?.avgPrice ??
          0
      );
      const qty = Math.abs(
        Number(trade?.volume ?? trade?.qty ?? trade?.quantity ?? trade?.size ?? trade?.shares ?? 0)
      );

      if (Number.isFinite(entryPrice) && entryPrice > 0 && Number.isFinite(qty) && qty > 0) {
        totalNotional += Math.abs(entryPrice * qty);
        totalQty += qty;
      }
    }

    return totalQty > 0 ? totalNotional / totalQty : null;
  }, [trades, timeRange]);

  const edgePct = useMemo(() => {
    if (!dynamicAvgPrice || dynamicAvgPrice === 0) return null;
    return (avgPerShare / dynamicAvgPrice) * 100;
  }, [avgPerShare, dynamicAvgPrice]);

  const formatEdge = (pct: number | null) => {
    if (pct === null || Number.isNaN(pct)) return 'N/A';
    const abs = Math.abs(pct);
    const precision = abs < 1 ? 3 : 2;
    const formatted = pct.toFixed(precision);
    return `${pct > 0 ? '+' : ''}${formatted}% Yield`;
  };

  const [showYieldDetails, setShowYieldDetails] = useState(false);

  const isGain = avgPerShare >= 0;
  const pillClasses = isGain
    ? 'bg-emerald-600/10 text-emerald-600 dark:text-emerald-200 border border-emerald-600/40'
    : 'bg-red-500/10 text-red-500 dark:text-red-500 border border-red-500/40';

  return (
    <div className="relative flex h-full w-full flex-col px-2 pb-0 pt-0 text-zinc-900 dark:text-white">
      <div className="flex items-center justify-center gap-3 pb-1">
        <span className={`text-4xl sm:text-5xl -mt-1.5 font-bold tracking-tight leading-none ${isGain ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-500'}`}>
          {fmtMoney(avgPerShare)}
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={() => setShowYieldDetails(s => !s)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowYieldDetails(s => !s); }}
          className={`px-3 py-1 text-sm rounded-md font-semibold border ${pillClasses} cursor-pointer select-none`}
        >
          {formatEdge(edgePct)}
        </span>
      </div>

      {/* Yield details (calculation) */}
      {showYieldDetails && (
        <div className="mt-2 text-center text-xs text-zinc-700 dark:text-zinc-300 px-2">
          {dynamicAvgPrice && edgePct !== null ? (
            (() => {
              const abs = Math.abs(edgePct);
              const precision = abs < 1 ? 3 : 2;
              return (
                <div>
                  <div>Edge calculation:</div>
                  <div className="mt-1">
                    {fmtMoney(avgPerShare)} ÷ {fmtMoney(dynamicAvgPrice)} × 100 = <span className="font-medium">{(edgePct).toFixed(precision)}%</span>
                  </div>
                </div>
              );
            })()
          ) : (
            <div>Average entry price unavailable to compute yield.</div>
          )}
        </div>
      )}

      <div
        data-testid="average-per-share-footer"
        className="mt-auto flex w-full items-center justify-between gap-2 whitespace-nowrap border-t border-white/10 pt-1 leading-none"
      >
        <div className="flex min-w-0 items-center gap-1">
          <Layers className="h-3 w-3 text-zinc-700 dark:text-zinc-500" aria-hidden />
          <span className="text-[9px] font-medium uppercase tracking-wide text-zinc-700 dark:text-zinc-500">Volume:</span>
          <span className="text-[11px] font-semibold text-zinc-900 dark:text-white">{totalVolume.toLocaleString()}</span>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <span className="text-[9px] font-medium uppercase tracking-wide text-zinc-700 dark:text-zinc-500">Total Impact:</span>
          <span className={`text-[11px] font-semibold ${impact >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-500'}`}>{fmtMoney(impact)}</span>
        </div>
      </div>
    </div>
  );
};
